import { z } from 'zod';

import { zId, zOptionalString } from './common';

/**
 * Доли людей в доходах организации (проценты с категорий).
 *
 * Игрок получает не только зарплату. С призовых, мерча, стриминга ему
 * причитается процент, и у каждого он свой: у звезды состава одна доля,
 * у новичка другая, у тренера третья. Пока эти проценты живут только в
 * договоре, их считают вручную при каждом поступлении — то есть тогда,
 * когда деньги уже пришли и всем не до расчётов. Что-нибудь неизбежно
 * оказывается посчитано не так.
 *
 * Процент — доля человека. Остальное остаётся организации: отдельно её
 * долю не храним, иначе появилось бы два числа, которые обязаны сходиться,
 * и однажды они разойдутся.
 */

export const upsertRevenueShareSchema = z.object({
  categoryId: zId,
  /** Доля человека в процентах. Ноль означает «не участвует». */
  percent: z.number().min(0).max(100),
  note: zOptionalString(200),
});
export type UpsertRevenueShareInput = z.infer<typeof upsertRevenueShareSchema>;

export interface RevenueShareDto {
  id: string;
  personId: string;
  personNickname: string;
  categoryId: string;
  categoryName: string;
  percent: number;
  note: string | null;
}

/** Строка предварительного расчёта: кому и сколько достанется с дохода. */
export interface DistributionRow {
  personId: string;
  personNickname: string;
  percent: number;
  amountMinor: number;
}

/**
 * Разбор поступления по долям.
 *
 * Показывается до начисления: человек должен видеть, кому сколько уйдёт,
 * прежде чем это станет обязательством организации.
 */
export interface DistributionPreview {
  transactionId: string;
  categoryId: string | null;
  categoryName: string | null;
  currency: string;
  amountMinor: number;
  rows: DistributionRow[];
  /** Что остаётся организации после всех долей. */
  orgMinor: number;
  /** Уже начислено по этому поступлению — чтобы не раздать дважды. */
  alreadyDistributed: boolean;
}

export const distributeSchema = z.object({
  /** Месяц, в выплату за который войдут доли. */
  periodKey: z.string().regex(/^\d{4}-\d{2}$/, { message: 'validation.invalidPeriod' }),
});
export type DistributeInput = z.infer<typeof distributeSchema>;
